import React, { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import { customerService } from '../../../services/customerService';

const ActiveOrderTracker = ({ order, customerProfile, onTrackOrder, onContactSupplier, onOrderUpdate }) => {
  const [customerName, setCustomerName] = useState(null);

  useEffect(() => {
    // Fetch real customer name for this order
    async function fetchCustomerName() {
      if (order?.id) {
        const { name } = await customerService.getCustomerNameForOrder(order.id);
        setCustomerName(name);
      }
    }
    fetchCustomerName();
  }, [order?.id]);
  useEffect(() => {
    if (!order?.id) return;

    // Set up real-time subscription for this specific order
    const channel = supabase?.channel(`order_${order?.id}_updates`)?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders',
          filter: `id=eq.${order?.id}`
        },
        (payload) => {
          console.log('Active order update received:', payload);
          
          // Notify parent component of the update
          if (onOrderUpdate) {
            onOrderUpdate(payload?.new);
          }
          
          // Show notification for status changes
          if (payload?.old?.status !== payload?.new?.status) {
            showOrderStatusNotification(payload?.new);
          }
        }
      )?.subscribe();

    // Cleanup subscription on unmount
    return () => {
      supabase?.removeChannel(channel);
    };
  }, [order?.id]);

  const showOrderStatusNotification = (updatedOrder) => {
    // Create a browser notification for order status changes
    if ('Notification' in window && Notification?.permission === 'granted') {
      const statusMessages = {
        pending: 'Order Confirmed! Your order has been received.',
        preparing: 'Order in Progress! Your meal is being prepared.',
        ready: 'Order Ready! Your order is ready for pickup/delivery.',
        delivered: 'Order Delivered! Enjoy your meal!',
        cancelled: 'Order Cancelled. Your order has been cancelled.'
      };

      new Notification('Order Update', {
        body: statusMessages?.[updatedOrder?.status] || 'Your order status has been updated.',
        icon: '/favicon.ico'
      });
    }
  };

  const getProgressPercentage = (status) => {
    switch (status) {
      case 'confirmed':
        return 25;
      case 'preparing':
        return 50;
      case 'ready':
        return 75;
      case 'in-transit':
        return 90;
      case 'delivered':
        return 100;
      default:
        return 0;
    }
  };

  const orderSteps = [
    { key: 'confirmed', label: 'Confirmed', icon: 'CheckCircle2' },
    { key: 'preparing', label: 'Preparing', icon: 'ChefHat' },
    { key: 'ready', label: 'Ready', icon: 'Package' },
    { key: 'in-transit', label: 'On the way', icon: 'Truck' }
  ];

  const currentStepIndex = orderSteps?.findIndex(step => step?.key === order?.status);


  // Helper to get customer name (prefer fetched, fallback to profile, then guest)
  const getCustomerName = () => {
    if (customerName) return customerName;
    if (customerProfile?.full_name) return customerProfile.full_name;
    if (customerProfile?.fullName) return customerProfile.fullName;
    if (customerProfile?.name) return customerProfile.name;
    return 'Guest Customer';
  };

  return (
    <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-lg md:text-xl">
          Active Order
        </h3>
        <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-bold rounded-full">
          Order #{order?.orderId}
        </span>
      </div>
      <div className="bg-card rounded-lg p-4 mb-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
            <Image
              src={order?.dishImage}
              alt={order?.dishImageAlt}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-heading font-semibold text-base line-clamp-1 mb-1">
              {order?.dishName}
            </h4>
            <p className="text-sm text-muted-foreground line-clamp-1">
              {order?.supplierName}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Customer: {getCustomerName()}
            </p>
          </div>
        </div>

        <div className="relative mb-6">
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-500"
              style={{ width: `${getProgressPercentage(order?.status)}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2 mb-4">
          {orderSteps?.map((step, index) => (
            <div key={step?.key} className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-smooth ${
                  index <= currentStepIndex
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                <Icon name={step?.icon} size={18} />
              </div>
              <span className={`text-xs text-center font-caption ${
                index <= currentStepIndex ? 'text-foreground font-semibold' : 'text-muted-foreground'
              }`}>
                {step?.label}
              </span>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between text-sm mb-4 p-3 bg-muted/50 rounded-lg">
          <span className="text-muted-foreground">Estimated delivery:</span>
          <span className="font-heading font-semibold text-foreground">
            {order?.estimatedDelivery}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="default"
            size="sm"
            fullWidth
            iconName="MapPin"
            onClick={() => onTrackOrder(order)}
          >
            Track on Map
          </Button>
          <Button
            variant="outline"
            size="sm"
            fullWidth
            iconName="MessageSquare"
            onClick={() => onContactSupplier(order)}
          >
            Contact
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ActiveOrderTracker;